import { useTasks } from '@app/context/TaskContext';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { copyToClipboard } from '@app/utils/copyToClipboard';
import { AlertDialog, Loading, Spinner } from '@components/ui';
import { Company } from '@dieterApi/company/useCompanyQuery';
import {
  CustomTraining,
  useCustomTrainingShareTokenQuery,
  useCustomTrainingsQuery,
  useResetEmployeeCustomTrainingMutation,
  useSendAllCustomTrainingEmailsMutation,
} from '@dieterApi/customTraining/useCustomTrainings';
import { useEmployeeAddMutation } from '@dieterApi/employee/useEmployeeAddMutation';
import { useEmployeeArchiveMutation } from '@dieterApi/employee/useEmployeeArchiveMutation';
import { useEmployeeDeleteMutation } from '@dieterApi/employee/useEmployeeDeleteMutation';
import { useEmployeeImportMutation } from '@dieterApi/employee/useEmployeeImportMutation';
import { useEmployeeUpdateMutation } from '@dieterApi/employee/useEmployeeUpdateMutation';
import { useTrainingResetMutation } from '@dieterApi/employee/useTrainingResetMutation';
import {
  useSendAllTrainingEmailsMutation,
  useSendTrainingEmailMutation,
} from '@dieterApi/employee/useTrainingEmailMutation';
import { useQuestionnaireDocumentShareQuery } from '@dieterApi/questionnaire/useQuestionnaireDocumentShareQuery';
import { TaskType, Tasks } from '@dieterApi/task/useTaskQuery';
import { User, UserQuestionnaireApplicationTopic, UserTopicApplications } from '@dieterApi/user/useUserQuery';
import { Employee } from '@dieterApi/user/useUserQuery';
import { QuestionnaireState } from '@legalosApi/types/QuestionnaireState';
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { Button, Chip, CircularProgress, IconButton, Skeleton, TableCell, TableRow, Tooltip } from '@mui/material';
import MUIDataTable, { MUIDataTableMeta, MUIDataTableOptions, MUIDataTableProps } from 'mui-datatables';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import './employeetable.sass';
import { ImportModal, ImportRow } from './ImportModal';
import { MaxEmployeesReachedModal } from './MaxEmployeesReachedModal';
import { PendingEmailItem, SendAllModal } from './SendAllModal';

export function EmployeeTable() {
  const { t } = useTranslation();
  const { user, activeCompany, topics, questionnaires } = useUserWithCompany();
  const tasks = useTasks();

  const { enqueueSnackbar } = useSnackbar();
  const [updateEmployee] = useEmployeeUpdateMutation();
  const [deleteEmployee] = useEmployeeDeleteMutation();
  const [archiveEmployee] = useEmployeeArchiveMutation();
  const [addEmployee] = useEmployeeAddMutation();
  const [importEmployees] = useEmployeeImportMutation();
  const [sendAllEmails] = useSendAllTrainingEmailsMutation();
  const [sendAllCustomEmails] = useSendAllCustomTrainingEmailsMutation();
  const { data: customTrainingsData } = useCustomTrainingsQuery();
  const customTrainings = customTrainingsData?.customTrainings;
  const [sendingAll, setSendingAll] = useState(false);
  const [sendAllConfirmOpen, setSendAllConfirmOpen] = useState(false);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<Set<string>>(new Set());
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<MUIDataTableMeta | null>(null);
  const [archiveAlertOpen, setArchiveAlertOpen] = useState(false);
  const [archiveRow, setArchiveRow] = useState<MUIDataTableMeta | null>(null);
  const [resetTraining] = useTrainingResetMutation();
  const [resetCustomTraining] = useResetEmployeeCustomTrainingMutation();
  const [resetAlertOpen, setResetAlertOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<{ employee: EmployeeData; training: Training } | null>(null);
  const [resetting, setResetting] = useState(false);
  const blankEntry = { id: '', name: '', email: '', createdAt: '', trainings: [] };
  const [newEntry, setNewEntry] = useImmer<EmployeeData>(blankEntry);
  const [maxReachedModalOpen, setMaxReachedModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [conflictResolution, setConflictResolution] = useState<'update' | 'skip'>('update');
  const [importing, setImporting] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const maxEmployees = user?.maxEmployees ?? activeCompany?.maxEmployees ?? 50;
  const activeEmployeeCount = (activeCompany?.employees || []).filter((e) => !e.archivedAt).length;
  const maxEmployeesReached = activeEmployeeCount >= maxEmployees;

  // Clear the new-entry row when the user clicks anywhere outside it
  useEffect(() => {
    if (newEntry.id !== 'new') return;
    const handleMouseDown = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-new-employee]')) {
        setNewEntry(blankEntry);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [newEntry.id]);

  // when new input field on manual entry row is added, focus it
  const handleRef = useCallback<(node: HTMLInputElement) => void>((node) => {
    if (node !== null) {
      node.focus();
    } else {
      window.focus();
    }
  }, []);

  const employeeData = useMemo(() => {
    const parsedData = parseEmployeeData(user, tasks, activeCompany, topics, questionnaires, customTrainings);
    return newEntry.id ? [...parsedData, newEntry] : parsedData;
  }, [user, tasks, newEntry, activeCompany, topics, customTrainings]);

  const availableTrainings = useMemo(() => {
    const seen = new Set<string>();
    const result: Training[] = [];
    for (const emp of employeeData) {
      if (emp.id === 'new') continue;
      for (const t of emp.trainings) {
        if (!seen.has(t.applicationId)) {
          seen.add(t.applicationId);
          result.push(t);
        }
      }
    }
    return result;
  }, [employeeData]);

  const pendingEmailItems = useMemo(
    (): PendingEmailItem[] =>
      employeeData
        .filter((e) => e.id !== 'new' && !e.isArchived && !!e.email)
        .flatMap((e) =>
          e.trainings
            .filter((t) => !t.hasConfirmed || t.isStale)
            .map((t) => ({ name: e.name, email: e.email!, trainingName: t.name, applicationId: t.applicationId }))
        ),
    [employeeData]
  );

  const handleDelete = (tableMeta: MUIDataTableMeta) => {
    const employeeId = tableMeta.rowData[0];
    deleteEmployee({
      variables: { employeeId },
      context: { companyId: user?.company?.id || '' },
    })
      .then(() => {
        enqueueSnackbar(t('dashboard.training.employee.table.snackbars.delete_employee.success'), {
          variant: 'success',
        });
      })
      .catch((e) => {
        if (e.message.includes('Max employees')) {
          setMaxReachedModalOpen(true);
          enqueueSnackbar(t('dashboard.training.employee.table.maxtooltip'), { variant: 'error' });
        } else {
          enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
        }
      });
  };

  const handleArchive = (tableMeta: MUIDataTableMeta) => {
    const employeeId = tableMeta.rowData[0];
    archiveEmployee({ variables: { employeeId } })
      .then(() => {
        enqueueSnackbar(
          t(
            'dashboard.training.employee.table.snackbars.archive_employee.success',
            'Mitarbeiter archiviert'
          ),
          { variant: 'success' }
        );
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleResetTraining = async () => {
    if (!resetTarget) return;
    const { employee, training } = resetTarget;
    setResetting(true);
    try {
      if (training.customTrainingId) {
        await resetCustomTraining({
          variables: { employeeId: employee.id, customTrainingId: training.customTrainingId },
        });
      } else {
        await resetTraining({ variables: { employeeId: employee.id, applicationId: training.applicationId } });
      }
      enqueueSnackbar(
        t(
          'dashboard.training.employee.table.snackbars.reset_training.success',
          'Schulungsstatus von {{userName}} für „{{trainingName}}“ zurückgesetzt.',
          { userName: employee.name, trainingName: training.name }
        ),
        { variant: 'success' }
      );
    } catch (e: any) {
      enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
    } finally {
      setResetting(false);
      setResetTarget(null);
    }
  };

  const handleAdd = () => {
    if (maxEmployeesReached) {
      setMaxReachedModalOpen(true);
      return;
    }
    setNewEntry((draft) => {
      draft.id = 'new';
    });
  };

  const handleUpdateName = (employee: EmployeeData, name: string) => {
    if (!name) return;
    if (employee.name === name) return;
    updateEmployee({
      variables: { employeeId: employee.id, name },
      optimisticResponse: {
        updateEmployee: {
          id: employee.id,
          name,
          email: employee.email || null,
          createdAt: employee?.createdAt || '',
        },
      },
    })
      .then(() => {
        enqueueSnackbar(t('dashboard.training.employee.table.snackbars.update_name.success'), { variant: 'success' });
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleUpdateEmail = (employee: EmployeeData, email: string) => {
    if (employee.email === email) return;
    if (email && !isValidEmail(email)) {
      enqueueSnackbar(t('dashboard.training.employee.table.snackbars.invalid_email', 'Ungültige E-Mail-Adresse'), {
        variant: 'error',
      });
      return;
    }
    updateEmployee({
      variables: { employeeId: employee.id, email: email || null },
      optimisticResponse: {
        updateEmployee: {
          id: employee.id,
          name: employee.name,
          email: email || null,
          createdAt: employee?.createdAt || '',
        },
      },
    })
      .then(() => {
        enqueueSnackbar(
          t('dashboard.training.employee.table.snackbars.update_email.success', 'E-Mail erfolgreich geändert'),
          { variant: 'success' }
        );
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleSave = (name: string, email?: string, followUp: boolean = false) => {
    if (!name) return;
    if (email && !isValidEmail(email)) {
      enqueueSnackbar(t('dashboard.training.employee.table.snackbars.invalid_email', 'Ungültige E-Mail-Adresse'), {
        variant: 'error',
      });
      return;
    }
    addEmployee({
      variables: { name, email: email || undefined },
      context: { companyId: user?.company?.id || '' },
    })
      .then(() => {
        enqueueSnackbar(t('dashboard.training.employee.table.snackbars.save_employee.success'), { variant: 'success' });
        followUp
          ? setNewEntry((draft) => {
              draft.id = 'new';
            })
          : setNewEntry(blankEntry);
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
      });
  };

  const handleSendAllEmails = async () => {
    const trainings = availableTrainings.filter((t) => selectedApplicationIds.has(t.applicationId));
    if (!trainings.length) return;
    setSendingAll(true);
    let totalSent = 0;
    let totalSkipped = 0;
    const allErrors: string[] = [];
    try {
      for (const training of trainings) {
        if (training.customTrainingId) {
          const result = await sendAllCustomEmails({
            variables: { customTrainingId: training.customTrainingId, baseUrl: window.location.origin },
          });
          const r = result.data?.sendAllCustomTrainingEmails;
          totalSent += r?.sent ?? 0;
          totalSkipped += r?.skipped ?? 0;
          if (r?.errors?.length) allErrors.push(...r.errors);
          continue;
        }
        const result = await sendAllEmails({
          variables: {
            applicationId: training.type === 'Document' ? undefined : training.applicationId,
            localQuestionnaireId: training.type === 'Document' ? training.questionnaireId : undefined,
            documentNumber: training.documentNumber,
            trainingTitle: training.name,
            type: training.type,
            baseUrl: window.location.origin,
          },
        });
        const r = result.data?.sendAllTrainingEmails;
        totalSent += r?.sent ?? 0;
        totalSkipped += r?.skipped ?? 0;
        if (r?.errors?.length) allErrors.push(...r.errors);
      }
      if (allErrors.length && totalSent === 0) {
        enqueueSnackbar(allErrors[0], { variant: 'error' });
      } else {
        enqueueSnackbar(
          t(
            'dashboard.training.employee.table.snackbars.send_all_emails.success',
            '{{sent}} E-Mail(s) versendet, {{skipped}} übersprungen.',
            { sent: totalSent, skipped: totalSkipped }
          ),
          { variant: totalSent > 0 ? 'success' : 'info' }
        );
      }
    } catch (e: any) {
      enqueueSnackbar(e.message, { variant: 'error' });
    } finally {
      setSendingAll(false);
    }
  };

  const handleCSVDownloadTemplate = () => {
    const escape = (v: string) =>
      v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replace(/"/g, '""')}"` : v;
    const rows = employeeData
      .filter((e) => e.id !== 'new' && !e.isArchived)
      .map((e) => `${escape(e.name)},${escape(e.email ?? '')}`);
    const csv = ['Name,Email', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mitarbeiter-vorlage.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadStatusPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const today = new Date();
    const dateStr = today.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // Title
    doc.setFontSize(18);
    doc.setTextColor(30, 30, 30);
    doc.text('DSGVO Schulungsstatus', 14, 20);

    // Subtitle date
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(`Stand: ${dateStr}`, 14, 28);

    // Build flat rows, showing name/email only on the first training row per employee
    type TableRow = { cells: string[]; groupIndex: number; status: string };
    const tableRows: TableRow[] = [];
    let groupIndex = 0;

    for (const employee of employeeData) {
      if (employee.id === 'new') continue;
      const countString = employee.countString ?? `${employee.confirmedCount ?? 0}/${employee.totalCount ?? 0}`;

      if (employee.trainings.length === 0) {
        const nameSuffix = employee.isArchived ? ' (archiviert)' : '';
        tableRows.push({
          cells: [`${employee.name}${nameSuffix}`, employee.email ?? '', '—', '—', '', countString],
          groupIndex,
          status: '',
        });
      } else {
        employee.trainings.forEach((training, i) => {
          let status: string;
          if (training.hasConfirmed && !training.isStale) {
            status = 'Bestätigt';
          } else if (training.hasConfirmed && training.isStale) {
            status = 'Abgelaufen';
          } else {
            status = 'Ausstehend';
          }
          const date = training.createdAt ? new Date(training.createdAt).toLocaleDateString('de-DE') : '';
          // Only first row of each employee shows name/email and progress
          const nameSuffix = employee.isArchived ? ' (archiviert)' : '';
          const nameCell = i === 0 ? `${employee.name}${nameSuffix}` : '';
          const emailCell = i === 0 ? (employee.email ?? '') : '';
          const progressCell = i === 0 ? countString : '';
          tableRows.push({
            cells: [nameCell, emailCell, training.name, status, date, progressCell],
            groupIndex,
            status,
          });
        });
      }
      groupIndex++;
    }

    // Alternating group background colors
    const groupColors: [number, number, number][] = [
      [255, 255, 255],
      [245, 247, 250],
    ];

    autoTable(doc, {
      head: [['Mitarbeiter', 'E-Mail', 'Schulung', 'Status', 'Bestätigt am', 'Fortschritt']],
      body: tableRows.map((r) => r.cells),
      startY: 35,
      styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: [60, 60, 60], textColor: 255, fontStyle: 'bold', overflow: 'hidden' },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 50 },
        2: { cellWidth: 80 },
        3: { cellWidth: 28 },
        4: { cellWidth: 34 },
        5: { cellWidth: 30 },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        const row = tableRows[data.row.index];
        if (!row) return;

        // Alternating group background
        data.cell.styles.fillColor = groupColors[row.groupIndex % 2];

        // Color-code the Status column (index 3)
        if (data.column.index === 3) {
          if (row.status === 'Bestätigt') {
            data.cell.styles.textColor = [34, 139, 34];
            data.cell.styles.fontStyle = 'bold';
          } else if (row.status === 'Abgelaufen') {
            data.cell.styles.textColor = [180, 60, 60];
            data.cell.styles.fontStyle = 'bold';
          } else if (row.status === 'Ausstehend') {
            data.cell.styles.textColor = [180, 110, 0];
          }
        }
      },
    });

    // Footer on every page
    const footerText = `Erstellt am: ${today.toLocaleDateString('de-DE')} um ${today.toLocaleTimeString('de-DE')}`;
    const pageCount = (doc.internal as any).getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text(footerText, doc.internal.pageSize.getWidth() - 14, doc.internal.pageSize.getHeight() - 8, {
        align: 'right',
      });
    }

    doc.save(`schulungsstatus-${today.toISOString().slice(0, 10)}.pdf`);
  };

  const handleCSVImport = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (ev) => {
      const csvString = ev.target?.result as string;
      if (!csvString) {
        enqueueSnackbar(
          t(
            'dashboard.training.employee.table.snackbars.csv_import.parse_error',
            'CSV-Datei konnte nicht gelesen werden.'
          ),
          { variant: 'error' }
        );
        return;
      }
      const parsed = parseImportCSV(csvString);
      if (parsed.length === 0) {
        enqueueSnackbar(
          t(
            'dashboard.training.employee.table.snackbars.csv_import.parse_error',
            'CSV-Datei konnte nicht gelesen werden.'
          ),
          { variant: 'error' }
        );
        return;
      }
      const existingEmployees = activeCompany?.employees ?? [];
      const rows: ImportRow[] = parsed.map(({ name, email, invalidEmail }) => {
        const existing = existingEmployees.find((emp) => emp.name === name);
        return {
          name,
          email,
          hasConflict: !!existing,
          existingEmail: existing?.email ?? undefined,
          invalidEmail,
        };
      });
      setImportRows(rows);
      setConflictResolution('update');
      setImportModalOpen(true);
    };
    reader.onerror = () => {
      enqueueSnackbar(
        t(
          'dashboard.training.employee.table.snackbars.csv_import.parse_error',
          'CSV-Datei konnte nicht gelesen werden.'
        ),
        { variant: 'error' }
      );
    };
    reader.readAsText(file);
  };

  const handleConfirmImport = async () => {
    const newRows = importRows.filter((r) => !r.hasConflict);
    const conflictRows = conflictResolution === 'update' ? importRows.filter((r) => r.hasConflict) : [];

    if (newRows.length === 0 && conflictRows.length === 0) {
      setImportModalOpen(false);
      return;
    }

    setImporting(true);
    try {
      // Update conflicting employees directly — bypasses the max-employees cap
      let updated = 0;
      for (const row of conflictRows) {
        const existing = activeCompany?.employees.find((e) => e.name === row.name);
        const emailToSet = row.invalidEmail ? '' : row.email;
        if (existing && emailToSet && existing.email !== emailToSet) {
          await updateEmployee({ variables: { employeeId: existing.id, email: emailToSet } });
          updated++;
        }
      }

      // Import new employees via the mutation
      let imported = 0;
      let skipped = 0;
      if (newRows.length > 0) {
        const escape = (v: string) =>
          v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replace(/"/g, '""')}"` : v;
        const csv = [
          'Name,Email',
          ...newRows.map((r) => `${escape(r.name)},${escape(r.invalidEmail ? '' : r.email)}`),
        ].join('\n');
        const { data } = await importEmployees({ variables: { csv } });
        const r = data?.importEmployees;
        if (r) {
          imported = r.imported;
          skipped = r.skipped;
          r.errors.forEach((err) => enqueueSnackbar(err, { variant: 'warning' }));
        }
      }

      enqueueSnackbar(
        t(
          'dashboard.training.employee.table.snackbars.csv_import.success',
          '{{imported}} Mitarbeiter importiert, {{skipped}} übersprungen.',
          { imported: imported + updated, skipped }
        ),
        { variant: imported + updated > 0 ? 'success' : 'info' }
      );
      setImportModalOpen(false);
    } catch (e: any) {
      enqueueSnackbar(e.message, { variant: 'error', stack: e.stack });
    } finally {
      setImporting(false);
    }
  };

  const columns: MUIDataTableProps['columns'] = [
    {
      name: 'id',
      label: t('dashboard.training.employee.table.columns.id.label'),
      options: { display: 'excluded' },
    },
    {
      name: 'name',
      label: t('dashboard.training.employee.table.columns.name.label'),
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const employeeId = tableMeta.rowData[0];
          const isNew = employeeId === 'new';
          const employee = employeeData.find((e) => e.id === employeeId);
          const isArchived = !!employee?.isArchived;
          const originalValue = employee?.name || '';

          return (
            <div className="flex items-center gap-2">
              <input
                data-testid="employee-table-name"
                data-new-employee={isNew ? 'true' : undefined}
                className={`w-full bg-transparent text-sm ${isArchived ? 'text-gray-400 line-through' : ''}`}
                ref={isNew ? handleRef : null}
                placeholder={t('dashboard.training.employee.table.columns.name.body.input_placeholder')}
                type="text"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore
                data-form-type="other"
                value={value}
                readOnly={isArchived}
                disabled={isArchived}
                onChange={(e) => {
                  if (isArchived) return;
                  updateValue(e.target.value);
                  if (isNew)
                    setNewEntry((draft) => {
                      draft.name = e.target.value;
                    });
                }}
                onBlur={(e) => {
                  if (isArchived) return;
                  if (!isNew) handleUpdateName(employee!, e.target.value);
                }}
                onKeyPress={(e) => {
                  if (isArchived) return;
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isNew) {
                      handleSave(
                        value,
                        newEntry.email || undefined,
                        !maxEmployeesReached && activeEmployeeCount < maxEmployees - 1
                      );
                    } else {
                      handleUpdateName(employee!, value);
                    }
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.blur();
                  }
                }}
              />
              {isArchived && (
                <Chip
                  size="small"
                  label={t('dashboard.training.employee.table.columns.name.archived_badge', 'Archiviert')}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'email',
      label: t('dashboard.training.employee.table.columns.email.label', 'E-Mail'),
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const employeeId = tableMeta.rowData[0];
          const isNew = employeeId === 'new';
          const employee = employeeData.find((e) => e.id === employeeId);
          const isArchived = !!employee?.isArchived;
          const emailInvalid = !!value && !isValidEmail(value);

          return (
            <div>
              <input
                data-testid={isNew ? 'employee-table-new-email' : 'employee-table-email'}
                data-new-employee={isNew ? 'true' : undefined}
                className={`w-full bg-transparent text-sm ${emailInvalid ? 'border-b border-red-500 text-red-500' : ''} ${isArchived ? 'text-gray-400 line-through' : ''}`}
                placeholder={t(
                  'dashboard.training.employee.table.columns.email.body.input_placeholder',
                  'E-Mail (optional)'
                )}
                type="text"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore
                data-form-type="other"
                value={value || ''}
                readOnly={isArchived}
                disabled={isArchived}
                onChange={(e) => {
                  if (isArchived) return;
                  if (isNew) {
                    setNewEntry((draft) => {
                      draft.email = e.target.value;
                    });
                  }
                  updateValue(e.target.value);
                }}
                onBlur={(e) => {
                  if (isArchived) return;
                  if (!isNew && employee) {
                    handleUpdateEmail(employee, e.target.value);
                  }
                }}
                onKeyPress={(e) => {
                  if (isArchived) return;
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isNew) {
                      handleSave(
                        tableMeta.rowData[1],
                        value || undefined,
                        !maxEmployeesReached && activeEmployeeCount < maxEmployees - 1
                      );
                    } else if (employee) {
                      handleUpdateEmail(employee, value || '');
                    }
                  }
                }}
              />
            </div>
          );
        },
      },
    },
    {
      name: 'countString',
      label: t('dashboard.training.employee.table.columns.countString.label'),
      options: {
        customBodyRender: (value, tableMeta) => {
          if (!tasks) return <Spinner size="small" />;
          const totalCount: number = tableMeta.rowData[5];
          const confirmedCount: number = tableMeta.rowData[6];
          const staleCount: number = tableMeta.rowData[7] ?? 0;
          const notStarted = confirmedCount === 0 && staleCount === 0;
          const fullComplete = confirmedCount === totalCount;
          const hasStale = staleCount > 0;
          return (
            <div className="flex gap-[10px]">
              <span>{value}</span>
              {fullComplete ? (
                <CheckCircleOutlineIcon fontSize="small" color="success" />
              ) : hasStale && confirmedCount + staleCount === totalCount ? (
                // All trainings done but some are stale — show stale warning
                <Tooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                  title={t(
                    'dashboard.training.employee.table.columns.countString.body.states.stale.tooltip',
                    'Einige Schulungen müssen erneuert werden'
                  )}
                >
                  <HistoryIcon fontSize="small" color="warning" />
                </Tooltip>
              ) : (
                <Tooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                  title={t('dashboard.training.employee.table.columns.countString.body.states.not_completed.tooltip')}
                >
                  <ErrorOutlineIcon fontSize="small" color={notStarted ? 'error' : 'secondary'} />
                </Tooltip>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'actions',
      label: t('dashboard.training.employee.table.columns.actions.label'),
      options: {
        customBodyRender: (value, tableMeta) => {
          const employeeId = tableMeta.rowData[0];
          const name = tableMeta.rowData[1];
          const isNew = employeeId === 'new';
          const employee = employeeData.find((e) => e.id === employeeId);
          const isArchived = !!employee?.isArchived;

          return (
            <div className="dtEmployeeTable__action flex gap-1">
              {!isNew && !isArchived && (
                <Tooltip
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                  title={t(
                    'dashboard.training.employee.table.columns.actions.body.states.existed_employee.archive_tooltip',
                    'Mitarbeiter archivieren (Schulungs-Dokumentation bleibt erhalten)'
                  )}
                >
                  <IconButton
                    color="info"
                    size="small"
                    onClick={() => {
                      setArchiveRow(tableMeta);
                      setArchiveAlertOpen(true);
                    }}
                    data-testid={`button-action-employee-archive-${tableMeta.rowIndex}`}
                  >
                    <ArchiveOutlinedIcon fontSize="small" data-testid="action-employee-btn-archive-icon" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                enterTouchDelay={0}
                leaveTouchDelay={5000}
                title={t(
                  `dashboard.training.employee.table.columns.actions.body.states.${isNew ? 'new_employee' : 'existed_employee'}.tooltip`
                )}
              >
                <IconButton
                  className="dtEmployeeTable__deleteButton"
                  color="info"
                  size="small"
                  onClick={() => {
                    if (isNew) {
                      handleSave(name, newEntry.email || undefined);
                    } else {
                      setDeleteRow(tableMeta);
                      setDeleteAlertOpen(true);
                    }
                  }}
                  data-testid={`buton-action-employee-${tableMeta.rowIndex}`}
                  data-new-employee={isNew ? 'true' : undefined}
                >
                  {isNew ? (
                    <SaveIcon fontSize="small" data-testid="action-employee-btn-save-icon" />
                  ) : (
                    <DeleteIcon fontSize="small" data-testid="action-employee-btn-delete-icon" />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          );
        },
        customHeadLabelRender: () => '',
      },
    },
    {
      name: 'totalCount',
      options: { display: 'excluded' },
    },
    {
      name: 'confirmedCount',
      options: { display: 'excluded' },
    },
    {
      name: 'staleCount',
      options: { display: 'excluded' },
    },
  ];

  const options: MUIDataTableOptions = {
    download: false,
    print: false,
    elevation: 0,
    selectableRows: 'none',
    selectableRowsHeader: false,
    expandableRows: true,
    isRowExpandable: (dataIndex) => employeeData[dataIndex]?.trainings.length > 0,
    expandableRowsOnClick: true,

    renderExpandableRow: (rowData, rowMeta) => {
      const colSpan = rowData.length + 1;
      const employee = employeeData[rowMeta.dataIndex];
      if (!employee) return null;

      return (
        <TableRow>
          <TableCell colSpan={colSpan}>
            <div className="dtEmployeeTable__expandableRow">
              {employee.trainings.map((training) => {
                const parsedDate = training.createdAt ? new Date(training.createdAt) : null;
                const date = parsedDate?.toLocaleDateString('de-DE');
                return (
                  <div key={training.applicationId} className="expandableRows">
                    <div className="rowItem flex-1">{training.name}</div>
                    <TrainingLink training={training} employee={employee} />
                    <div className="rowItem flex-none w-20 text-sm">{date ?? ''}</div>
                    <div className="rowItem">
                      {training.hasConfirmed && !training.isStale ? (
                        <CheckCircleOutlineIcon fontSize="small" color="success" data-testid="check-icon" />
                      ) : training.hasConfirmed && training.isStale ? (
                        <Tooltip
                          enterTouchDelay={0}
                          leaveTouchDelay={5000}
                          title={t(
                            training.staleReason === 'content'
                              ? 'dashboard.training.employee.table.expandable_row.training_records.tooltips.stale_content'
                              : 'dashboard.training.employee.table.expandable_row.training_records.tooltips.stale_time',
                            training.staleReason === 'content'
                              ? 'Schulungsinhalt wurde aktualisiert — erneute Durchführung erforderlich'
                              : 'Schulung abgelaufen — erneute Durchführung erforderlich'
                          )}
                        >
                          <HistoryIcon fontSize="small" color="warning" data-testid="stale-icon" />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          enterTouchDelay={0}
                          leaveTouchDelay={5000}
                          title={t(
                            'dashboard.training.employee.table.expandable_row.training_records.tooltips.not_confirmed'
                          )}
                        >
                          <ErrorOutlineIcon fontSize="small" color="error" />
                        </Tooltip>
                      )}
                    </div>
                    <div className="rowItem flex-none w-8">
                      {training.hasConfirmed && !employee.isArchived && (
                        <Tooltip
                          enterTouchDelay={0}
                          leaveTouchDelay={5000}
                          title={t(
                            'dashboard.training.employee.table.expandable_row.reset_training.tooltip',
                            'Schulungsstatus zurücksetzen'
                          )}
                        >
                          <span>
                            <IconButton
                              size="small"
                              data-testid="reset-training-button"
                              disabled={user?.isReadOnly || resetting}
                              onClick={(e) => {
                                e.stopPropagation();
                                setResetTarget({ employee, training });
                                setResetAlertOpen(true);
                              }}
                            >
                              <RestartAltIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </TableCell>
        </TableRow>
      );
    },
    customToolbar: () => (
      <div className="flex gap-2 items-center">
        <Tooltip
          title={t(
            'dashboard.training.employee.table.toolbar.actions.send_all_emails',
            'Schulungs-Erinnerung an alle ausstehenden MitarbeiterInnen senden'
          )}
        >
          <span>
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedApplicationIds(new Set(availableTrainings.map((t) => t.applicationId)));
                setSendAllConfirmOpen(true);
              }}
              startIcon={sendingAll ? <CircularProgress size={14} /> : <EmailIcon />}
              disabled={user?.isReadOnly || sendingAll || !employeeData.some((e) => e.id !== 'new')}
              size="small"
            >
              {t('dashboard.training.employee.table.toolbar.actions.send_all_emails_label', 'Alle benachrichtigen…')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={t(
            'dashboard.training.employee.table.toolbar.actions.download_status',
            'Schulungsstatus als PDF herunterladen'
          )}
        >
          <span>
            <Button
              variant="outlined"
              onClick={handleDownloadStatusPDF}
              startIcon={<DownloadIcon />}
              disabled={!employeeData.some((e) => e.id !== 'new')}
              size="small"
            >
              {t('dashboard.training.employee.table.toolbar.actions.download_status', 'Status exportieren')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={maxEmployeesReached ? t('dashboard.training.employee.table.maxtooltip') : ''}>
          <span>
            <Button
              variant="outlined"
              onClick={handleAdd}
              startIcon={<AddIcon />}
              data-testid="add-employee-button"
              disabled={user?.isReadOnly}
              size="small"
            >
              {t('dashboard.training.employee.table.toolbar.actions.add')}
            </Button>
          </span>
        </Tooltip>
      </div>
    ),
    search: false,
    filter: false,
    viewColumns: false,
    pagination: false,
    textLabels: {
      selectedRows: {
        text: t('dashboard.training.employee.table.textLabels.selectedRows.text'),
        delete: t('dashboard.training.employee.table.textLabels.selectedRows.delete'),
        deleteAria: t('dashboard.training.employee.table.textLabels.selectedRows.deleteAria'),
      },
      body: {
        noMatch: t('dashboard.training.employee.table.textLabels.body.noMatch'),
      },
    },
  };

  return (
    <div className="dtEmployeeTable">
      {employeeData ? (
        <MUIDataTable
          columns={columns}
          data={employeeData}
          title={
            // Kein Titeltext: die Überschrift "Mitarbeiter" steht schon darüber.
            <div className="flex items-center gap-1">
              <Tooltip
                title={t(
                  'dashboard.training.employee.table.toolbar.actions.download_template',
                  'Mitarbeiter als CSV exportieren'
                )}
              >
                <IconButton onClick={handleCSVDownloadTemplate} size="small">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('dashboard.training.employee.table.toolbar.actions.import_csv', 'Importieren')}>
                <span>
                  <IconButton onClick={() => csvInputRef.current?.click()} disabled={user?.isReadOnly} size="small">
                    <UploadFileIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleCSVImport}
              />
            </div>
          }
          options={options}
        />
      ) : (
        <Loading />
      )}
      <AlertDialog
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        message={
          <Trans
            t={t}
            i18nKey="dashboard.training.employee.table.dialogs.delete.message"
            components={{ bold: <b /> }}
            shouldUnescape
            values={{ userName: deleteRow?.rowData[1] }}
          />
        }
        onAccept={() => deleteRow && handleDelete(deleteRow)}
      />
      <AlertDialog
        open={archiveAlertOpen}
        setOpen={setArchiveAlertOpen}
        title={t('dashboard.training.employee.table.dialogs.archive.title', 'Mitarbeiter archivieren')}
        message={
          <Trans
            t={t}
            i18nKey="dashboard.training.employee.table.dialogs.archive.message"
            components={{ bold: <b /> }}
            shouldUnescape
            values={{ userName: archiveRow?.rowData[1] }}
          />
        }
        acceptText={t('dashboard.training.employee.table.dialogs.archive.acceptText', 'Archivieren')}
        onAccept={() => archiveRow && handleArchive(archiveRow)}
      />
      <AlertDialog
        open={resetAlertOpen}
        setOpen={setResetAlertOpen}
        title={t('dashboard.training.employee.table.dialogs.reset_training.title', 'Schulungsstatus zurücksetzen')}
        message={
          <Trans
            t={t}
            i18nKey="dashboard.training.employee.table.dialogs.reset_training.message"
            components={{ bold: <b /> }}
            shouldUnescape
            values={{ userName: resetTarget?.employee.name, trainingName: resetTarget?.training.name }}
          />
        }
        acceptText={t('dashboard.training.employee.table.dialogs.reset_training.acceptText', 'Zurücksetzen')}
        onAccept={handleResetTraining}
      />
      <SendAllModal
        open={sendAllConfirmOpen}
        trainings={availableTrainings}
        selectedApplicationIds={selectedApplicationIds}
        onSelectionChange={setSelectedApplicationIds}
        pendingItems={pendingEmailItems}
        onClose={() => setSendAllConfirmOpen(false)}
        onConfirm={() => {
          setSendAllConfirmOpen(false);
          handleSendAllEmails();
        }}
        sending={sendingAll}
      />
      <MaxEmployeesReachedModal open={maxReachedModalOpen} onClose={() => setMaxReachedModalOpen(false)} />
      <ImportModal
        open={importModalOpen}
        rows={importRows}
        conflictResolution={conflictResolution}
        onConflictResolutionChange={setConflictResolution}
        onClose={() => setImportModalOpen(false)}
        onConfirm={handleConfirmImport}
        loading={importing}
      />
    </div>
  );
}

interface TrainingLinkProps {
  training: Training;
  employee: EmployeeData;
}

const TrainingLink = ({ training, employee }: TrainingLinkProps) => {
  const { t } = useTranslation();
  const [getToken, { data: tokenData, loading: questionnaireTokenLoading }] = useQuestionnaireDocumentShareQuery();
  const [getCustomToken, { data: customTokenData, loading: customTokenLoading }] = useCustomTrainingShareTokenQuery();
  const loading = questionnaireTokenLoading || customTokenLoading;
  const shareToken = training.customTrainingId
    ? customTokenData?.getCustomTrainingShareToken
    : tokenData?.getQuestionnaireShareToken;
  const [sendEmail, { loading: sendLoading }] = useSendTrainingEmailMutation();
  const [hasCopied, setHasCopied] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (training.type === 'CustomTraining') {
      training.customTrainingId && getCustomToken({ variables: { customTrainingId: training.customTrainingId } });
      return;
    }
    getToken({
      variables: {
        localQuestionnaireId: training.type === 'Document' ? training.questionnaireId : undefined,
        applicationId: training.type === 'Document' ? undefined : training.applicationId,
        type: training.type,
        documentNumber: training.documentNumber,
      },
    });
  }, []);

  const buildPersonalizedUrl = (baseToken: string) => {
    const nameB64 = btoa(unescape(encodeURIComponent(employee.name)));
    return `${window.location.origin}/share?token=${baseToken}&name=${encodeURIComponent(nameB64)}`;
  };

  const handleCopyLink = () => {
    if (shareToken && !loading) {
      copyToClipboard(buildPersonalizedUrl(shareToken)).then(() => setHasCopied(true));
    }
  };

  const handleSendEmail = () => {
    const token = shareToken;
    if (!token) return;
    if (!employee.email) {
      enqueueSnackbar(
        t(
          'dashboard.training.employee.table.snackbars.send_email.no_email',
          'Dieser Mitarbeiter hat keine E-Mail-Adresse.'
        ),
        { variant: 'warning' }
      );
      return;
    }
    const url = buildPersonalizedUrl(token);
    sendEmail({
      variables: {
        employeeId: employee.id,
        trainingUrl: url,
        trainingTitle: training.name,
      },
    })
      .then(({ data }) => {
        const r = data?.sendTrainingEmail;
        if (r?.sent) {
          enqueueSnackbar(
            t('dashboard.training.employee.table.snackbars.send_email.success', 'E-Mail erfolgreich versendet.'),
            { variant: 'success' }
          );
        } else {
          enqueueSnackbar(r?.errors[0] || 'Fehler beim Versenden', { variant: 'error' });
        }
      })
      .catch((e) => enqueueSnackbar(e.message, { variant: 'error' }));
  };

  if (loading) return <Skeleton width={150} height={24} />;

  const token = shareToken;
  const personalizedUrl = token ? buildPersonalizedUrl(token) : '';
  const isArchived = !!employee.isArchived;

  if (isArchived) {
    return (
      <div className="flex items-center gap-1">
        <Tooltip
          enterTouchDelay={0}
          leaveTouchDelay={5000}
          title={t(
            'dashboard.training.employee.table.expandable_row.archived.tooltip',
            'Archivierter Mitarbeiter — Schulungslinks deaktiviert'
          )}
        >
          <span className="text-sm text-gray-400 italic">
            {t('dashboard.training.employee.table.expandable_row.archived.label', 'Archiviert')}
          </span>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Tooltip
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        title={
          hasCopied
            ? t('dashboard.topic.process.publishprocess.copied')
            : t('dashboard.topic.process.publishprocess.clipboard')
        }
      >
        <IconButton
          onClick={handleCopyLink}
          onMouseLeave={() => setTimeout(() => setHasCopied(false), 1000)}
          size="small"
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={'In neuem Fenster öffnen'}>
        <a
          className="underline text-primary-root hover:text-primary-800 flex items-center gap-1"
          href={personalizedUrl}
          target="_blank"
          rel="noreferrer"
        >
          {t('dashboard.training.employee.table.link')}
          <OpenInNewIcon fontSize="inherit" className="text-gray-400" />
        </a>
      </Tooltip>
      <Tooltip
        enterTouchDelay={0}
        leaveTouchDelay={5000}
        title={
          employee.email
            ? t(
                'dashboard.training.employee.table.expandable_row.send_email.tooltip',
                'Schulungslink per E-Mail senden'
              )
            : t(
                'dashboard.training.employee.table.expandable_row.send_email.no_email_tooltip',
                'Keine E-Mail-Adresse hinterlegt'
              )
        }
      >
        <span>
          <IconButton
            onClick={handleSendEmail}
            disabled={sendLoading || !token}
            size="small"
            color={employee.email ? 'primary' : 'default'}
          >
            {sendLoading ? <CircularProgress size={16} /> : <EmailIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

interface EmployeeData {
  id: string;
  name: string;
  email?: string | null;
  createdAt: string;
  archivedAt?: string | null;
  isArchived?: boolean;
  confirmedCount?: number;
  staleCount?: number;
  totalCount?: number;
  countString?: string;
  trainings: Training[];
}

export interface Training {
  name: string;
  createdAt: string;
  hasConfirmed: boolean;
  isStale: boolean;
  staleReason?: string | null;
  questionnaireId: string;
  applicationId: string;
  documentNumber: number;
  type: 'Document' | 'Questionnaire' | 'CustomTraining';
  // set for custom trainings (uploaded documents); applicationId then holds a synthetic `custom:<id>` key
  customTrainingId?: string;
}

const AllShareTaskTypes = [TaskType.PUBLISHPROCESS, TaskType.SHAREQUESTIONNAIRE];

const parseEmployeeData = (
  user: User | undefined,
  tasks: Tasks | undefined,
  activeCompany: Company | undefined,
  topics: UserTopicApplications[],
  allQuestionnaires: UserQuestionnaireApplicationTopic[] | undefined,
  customTrainings: CustomTraining[] | undefined
): EmployeeData[] => {
  const publishTasks = tasks?.filter((t) => AllShareTaskTypes.includes(t.type)) || [];
  const publishApplications = publishTasks.map((t) => t.applicationId).filter(Boolean) as string[];

  const employeeData: EmployeeData[] =
    activeCompany?.employees.map((employee: Employee) => {
      const trainings: Training[] = publishApplications.map((app) => {
        const task = publishTasks.find((t) => t.applicationId === app);
        const questionnaires = allQuestionnaires?.filter((q) => q.application.id === app);
        const confirmedQuestionnaire = questionnaires?.find((q) =>
          q.documentConfirmations.some((dc) => dc.employeeId === employee.id)
        );
        const finishedQuestionnaire = questionnaires?.find((q) => q.state === QuestionnaireState.COMPLETE);

        const hasConfirmed = !!confirmedQuestionnaire;
        const confirmedDc = confirmedQuestionnaire?.documentConfirmations.find((dc) => dc.employeeId === employee.id);
        const createdAt = confirmedDc?.createdAt || '';
        const appTitle = topics.flatMap((t) => t.applications).find((a) => a.id === app)?.title || '';

        // Staleness is computed server-side and returned on the DocumentConfirmation
        const isStale = hasConfirmed ? (confirmedDc?.isStale ?? false) : false;
        const staleReason = hasConfirmed ? confirmedDc?.staleReason : undefined;

        const isQuestionnaireTask = task?.type === TaskType.SHAREQUESTIONNAIRE;
        return {
          name: appTitle,
          createdAt,
          hasConfirmed,
          isStale,
          staleReason,
          questionnaireId: (isQuestionnaireTask ? confirmedQuestionnaire?.id : finishedQuestionnaire?.id) || '',
          applicationId: app,
          documentNumber: task?.section || 0,
          type: (isQuestionnaireTask ? 'Questionnaire' : 'Document') as 'Document' | 'Questionnaire',
        };
      });

      for (const custom of customTrainings ?? []) {
        const confirmation = custom.confirmations.find((c) => c.employeeId === employee.id);
        trainings.push({
          name: custom.title,
          createdAt: confirmation?.createdAt || '',
          hasConfirmed: !!confirmation,
          isStale: confirmation?.isStale ?? false,
          staleReason: confirmation?.staleReason,
          questionnaireId: '',
          applicationId: `custom:${custom.id}`,
          documentNumber: 0,
          type: 'CustomTraining',
          customTrainingId: custom.id,
        });
      }

      const prelim = {
        id: employee.id,
        name: employee.name,
        email: employee.email,
        createdAt: employee.createdAt,
        archivedAt: employee.archivedAt ?? null,
        isArchived: !!employee.archivedAt,
        trainings,
        confirmedCount: trainings.filter((t) => t.hasConfirmed && !t.isStale).length,
        staleCount: trainings.filter((t) => t.isStale).length,
        totalCount: trainings.length,
      };
      return { ...prelim, countString: `${prelim.confirmedCount}/${prelim.totalCount}` };
    }) || [];

  employeeData.sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
  return employeeData;
};

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function splitCSVLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else inQuotes = !inQuotes;
    } else if (ch === delimiter && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parseImportCSV(csv: string): { name: string; email: string; invalidEmail: boolean }[] {
  const cleaned = csv.replace(/^\uFEFF/, '');
  const lines = cleaned.split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];

  // Auto-detect delimiter: whichever of ; or , appears more in the header wins
  const delimiter = lines[0].split(';').length >= lines[0].split(',').length ? ';' : ',';

  const header = splitCSVLine(lines[0], delimiter).map((h) => h.trim());
  const nameIdx = header.findIndex((h) => ['name', 'Name', 'Vorname Nachname'].includes(h));
  const emailIdx = header.findIndex((h) => ['email', 'Email', 'E-Mail', 'E-Mail Adresse'].includes(h));

  if (nameIdx === -1) return [];

  return lines
    .slice(1)
    .map((line) => {
      const cols = splitCSVLine(line, delimiter);
      const email = emailIdx >= 0 ? (cols[emailIdx]?.trim() ?? '') : '';
      return {
        name: cols[nameIdx]?.trim() ?? '',
        email,
        invalidEmail: !!email && !isValidEmail(email),
      };
    })
    .filter((r) => r.name);
}
